import React from 'react';
import { Link } from 'react-router-dom';

import {
  PromptPlate, DocPlate, FlowPlate, DesignPlate, EditPlate, PlanPlate, GitPlate, ScanPlate, LivePlate,
} from './ProcessFigures';

import './ProcessSection.css';

/*
 * The planning + execution process, grounded in the real pipeline:
 * supervisor-routed planning agents (SRD → user flows → designs →
 * architecture → task plan) with typed human approval gates, then
 * six-phase task execution in isolated workspaces and automatic
 * staging deploys.
 *
 * Static markup + CSS keyframes only — the sequence animation runs
 * without JavaScript and honors prefers-reduced-motion.
 */

interface Stage {
  num: string;
  icon: string;
  chip: string;
  who: 'you' | 'ai';
  title: string;
  body: string;
}

const STAGES: Stage[] = [
  {
    num: '01', icon: 'fa-solid fa-comment-dots', chip: 'You describe', who: 'you',
    title: 'Share your idea',
    body: `Type what you want to build. A project is created instantly, and a supervisor agent
      decides which specialists to bring in - requirements, design, user flows, architecture, tasks.`,
  },
  {
    num: '02', icon: 'fa-solid fa-file-lines', chip: 'You approve', who: 'you',
    title: 'Requirements, written down',
    body: `The System Requirements Agent writes your requirements document - the source of truth
      everything else derives from. Every later change arrives as a diff you accept or reject.`,
  },
  {
    num: '03', icon: 'fa-solid fa-diagram-project', chip: 'You approve', who: 'you',
    title: 'User flows, mapped',
    body: `The User Flow Planner turns requirements into screen-by-screen journeys for every user
      persona - and the pipeline pauses until you approve them.`,
  },
  {
    num: '04', icon: 'fa-solid fa-palette', chip: 'You approve', who: 'you',
    title: 'Designs you can click',
    body: `Your first clickable design preview lands in as little as two minutes. The home page is
      designed first and sets the style; approve it, and the remaining pages generate in parallel -
      planned, coded, screenshotted, and checked by an AI design critic.`,
  },
  {
    num: '05', icon: 'fa-solid fa-wand-magic-sparkles', chip: 'You edit', who: 'you',
    title: 'Edit it visually',
    body: `Click any element on a design page and change its text, font, size, colours, gradients
      and borders in an inspector - direct saves write straight to the code and cost no credits. Ask
      the Design Agent for anything bigger, and preview every page on desktop, tablet and mobile.`,
  },
  {
    num: '06', icon: 'fa-solid fa-sitemap', chip: 'You approve', who: 'you',
    title: 'Blueprint and task plan',
    body: `The System Architect draws five architecture diagrams; the Project Manager breaks the build
      into tasks, sprints and phases. Review it all in a guided walkthrough, then press Start Building.`,
  },
  {
    num: '07', icon: 'fa-solid fa-robot', chip: 'Agents build', who: 'ai',
    title: 'AI engineers, in parallel',
    body: `Each task runs in its own isolated cloud workspace through a six-phase pipeline - setup,
      environment, branch, code, tests, staging - and merges into your repo by pull request.`,
  },
  {
    num: '08', icon: 'fa-solid fa-shield-halved', chip: 'Agents verify', who: 'ai',
    title: 'Security scanned, every task',
    body: `Before a task can reach staging, its workspace is scanned for leaked secrets and API keys,
      vulnerable dependencies and unprotected API routes. Findings land on the task card with a one-click
      fix run, and Deploy warns until the latest scan is clean.`,
  },
  {
    num: '09', icon: 'fa-solid fa-rocket', chip: 'You open it', who: 'you',
    title: 'Your app, live on a URL',
    body: `Every finished task auto-deploys to your project’s own Kubernetes namespace - a managed
      backend with your API, database and storage already running. Open the live staging URL on any
      device, or press Deploy to promote the latest build to production.`,
  },
];

const PLATES: Record<string, React.FC> = {
  '01': PromptPlate,
  '02': DocPlate,
  '03': FlowPlate,
  '04': DesignPlate,
  '05': EditPlate,
  '06': PlanPlate,
  '07': GitPlate,
  '08': ScanPlate,
  '09': LivePlate,
};

const ProcessItem: React.FC<{ stage: Stage; index: number }> = ({ stage, index }) => {
  const Plate = PLATES[stage.num];

  return (
    <li
      id={`step-${stage.num}`}
      className={`ps-item ps-item--${stage.who}`}
      style={{ '--i': index } as React.CSSProperties}
    >
      <span className="ps-item__node">
        <i className={stage.icon} aria-hidden="true" />
      </span>
      <div className="ps-item__card">
        <div className="ps-item__text">
          <div className="ps-item__top">
            <span className="ps-item__num">{stage.num}</span>
            <h3 className="ps-item__title">{stage.title}</h3>
            <span className="ps-item__chip">{stage.chip}</span>
          </div>
          <p className="ps-item__body">{stage.body}</p>
        </div>
        <div className="ps-item__fig">
          <Plate />
        </div>
      </div>
    </li>
  );
};

export const ProcessSection: React.FC = () => (
  <section id="process" className="ps-section" aria-labelledby="process-heading">
    <div className="ps-inner">
      <div className="ps-header">
        <p className="ps-header__eyebrow">The process</p>
        <h2 id="process-heading" className="ps-header__heading">
          Not a black box. A process.
          <br />
          <span className="ps-header__sub">
            Nine steps from one sentence to a running product - a clickable design in as little as two
            minutes, a security scan on every task, agents do the work, you hold the gates.
          </span>
        </h2>
      </div>
      <ol className="ps-flow">
        {STAGES.map((s, i) => <ProcessItem key={s.num} stage={s} index={i} />)}
      </ol>
      <p className="ps-kicker">
        The same process runs behind every project in <Link to="/explore">the gallery</Link> - open one
        and read its requirements, designs and task board.
      </p>
    </div>
  </section>
);
